import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { io } from 'socket.io-client';
import { 
  Play, 
  DollarSign, 
  Users, 
  Clock, 
  TrendingUp,
  Trophy,
  BarChart3,
  Settings,
  Zap
} from 'lucide-react';
import CockFight3D from '../components/CockFight3D';
import Coin from '../components/Coin';
import BettersListCard from '../components/BettersListCard';
import HamburgerMenu from '../components/HamburgerMenu';
// Animation CSS pour le message de victoire/défaite
if (typeof document !== 'undefined' && !document.getElementById('fade-message-style')) {
  const fadeMessageStyle = `
    .fade-message {
      opacity: 0;
      transition: opacity 0.5s;
      position: fixed;
      top: 30px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 50;
      font-size: 1.25rem;
      font-weight: bold;
      padding: 1rem 2rem;
      border-radius: 0.5rem;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      pointer-events: none;
    }
    .fade-message.show {
      opacity: 1;
    }
  `;
  const style = document.createElement('style');
  style.id = 'fade-message-style';
  style.innerHTML = fadeMessageStyle;
  document.head.appendChild(style);
}

const WAIT_COUNTDOWN = 10;

const Game = () => {
  // ...existing code...
  const { user, token, updateBalance } = useAuthStore();
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState({
    status: 'waiting', // waiting, active, finished
    multiplier: 1.0,
    timeElapsed: 0,
    players: [],
    gameId: null,
    countdown: WAIT_COUNTDOWN 
  });
  
  // États pour les paris
  const [betAmount, setBetAmount] = useState('10');
  const [selectedCock, setSelectedCock] = useState('cock1'); // cock1 ou cock2
  const [myBet, setMyBet] = useState(null);
  const [canCashout, setCanCashout] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [userBets, setUserBets] = useState([]);
  // État pour la card menu sélectionnée
  const [menuCard, setMenuCard] = useState(null); // 'history', 'how-to-play', 'rules', null
  // Empêche le scroll du fond quand une card menu est ouverte
  useEffect(() => {
    if (menuCard) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [menuCard]);
  // Empêche le scroll du fond quand une card menu est ouverte
  useEffect(() => {
    if (menuCard) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [menuCard]);
  // ...existing code...
  // Récupère l'historique des mises utilisateur quand la card historique est ouverte
  useEffect(() => {
    if (menuCard === 'history' && user && token) {
      fetch(`/api/gameHistory/my-history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data.history)) setUserBets(data.history);
            else setError('Aucune donnée reçue pour l\'historique.');
          })
          .catch(err => {
            setError('Erreur lors de la récupération de l\'historique : ' + err.message);
        });
    }
  }, [menuCard, user]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  // États pour l'auto-cashout
  // Message de victoire/défaite
  const [resultMessage, setResultMessage] = useState(null); // { type: 'win'|'lose', text: string }
  const [autoCashoutEnabled, setAutoCashoutEnabled] = useState(false);
  const [autoCashoutMultiplier, setAutoCashoutMultiplier] = useState(2.0);
  // Valeur du multiplicateur où on a encaissé (null si pas encaissé)
  const [cashoutAt, setCashoutAt] = useState(null);
  
  // États pour les paris en attente
  const [pendingBet, setPendingBet] = useState(null);
  const [canPlaceBet, setCanPlaceBet] = useState(true);
  const [betType, setBetType] = useState(null); // 'cock', 'multiplier', or null
  const [nextRoundBetType, setNextRoundBetType] = useState(null);
  
  const multiplierRef = useRef(null);
  const animationRef = useRef(null);
  const autoCashoutEnabledRef = useRef(autoCashoutEnabled);
  const autoCashoutMultiplierRef = useRef(autoCashoutMultiplier);
  const myBetRef = useRef(myBet);
  const pendingBetRef = useRef(pendingBet);
  const lastAppliedGameIdRef = useRef(null);
  const nextRoundBetTypeRef = useRef(nextRoundBetType);
  const messageTimeoutRef = useRef(null);

  // Contrôle d'affichage temporaire de la carte joueurs
  // La carte ne s'affiche que quand le jeu est "active" (après le loader)
  const [showBettersCard, setShowBettersCard] = useState(false);

  // État pour le menu hamburger
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // État pour le popup de solde insuffisant
  const [showInsufficientBalancePopup, setShowInsufficientBalancePopup] = useState(false);

  // ✅ Fonction utilitaire pour nettoyer l'état de pari
  const resetBetState = () => {
    setMyBet(null);
    setCanCashout(false);
    setCashoutAt(null);
    setResultMessage(null);
  };

  // Montants prédéfinis
  const presetAmounts = ['MIN', 10, 25, 50, 100, 'MAX'];

  // Sync refs with latest state values used inside socket event handlers
  useEffect(() => { autoCashoutEnabledRef.current = autoCashoutEnabled; }, [autoCashoutEnabled]);
  useEffect(() => { autoCashoutMultiplierRef.current = autoCashoutMultiplier; }, [autoCashoutMultiplier]);
  useEffect(() => { myBetRef.current = myBet; }, [myBet]);
  useEffect(() => { pendingBetRef.current = pendingBet; }, [pendingBet]);
  useEffect(() => { nextRoundBetTypeRef.current = nextRoundBetType; }, [nextRoundBetType]);

  useEffect(() => {
    if (!user) return;

    // Connexion WebSocket

    const newSocket = io('web-production-58f69.up.railway.app/');

    setSocket(newSocket);

    // Événements de connexion
    newSocket.on('connect', () => {
      console.log('🔌 Connecté au serveur de jeu');
      setIsConnected(true);
      setError(null);
      
      // Rejoindre le jeu
      newSocket.emit('join-game', {
        userId: user.id,
        username: user.username
      });
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Déconnecté du serveur de jeu');
      setIsConnected(false);
    });

    // Événements du jeu
    newSocket.on('game-state', (data) => {
      const sanitizedPlayers = data && data.players ? (data.status === 'active' ? data.players : data.players.map(p => ({ ...p, bet: null }))) : [];
      setGameState({
        ...data,
        players: sanitizedPlayers
      });
    });

    newSocket.on('new-game', (data) => {
      console.log('🎮 Nouvelle partie:', data);
      setGameState(prev => ({
        ...prev,
        status: 'waiting',
        multiplier: 1.0,
        timeElapsed: 0,
        gameId: data.gameId,
        countdown: WAIT_COUNTDOWN,
        players: []
      }));
      setMyBet(null);
      setCanCashout(false);
      setCanPlaceBet(true);
      setBetType(null);
      
      // Si un pari est en attente, il sera posé automatiquement quand l'état passera à "waiting" (voir watcher)
    });

    newSocket.on('game-started', (data) => {
      console.log('🎮 Partie démarrée:', data);
      setGameState(prev => ({
        ...prev,
        status: 'active',
        timeElapsed: 0,
        countdown: 0
      }));
      setCanCashout(true);
      setCanPlaceBet(false);
    });

    newSocket.on('multiplier-update', (data) => {
      setGameState(prev => ({
        ...prev,
        multiplier: data.multiplier,
        timeElapsed: data.timeElapsed
      }));
      
    // Vérifier l'auto-cashout (uniquement pour les paris de type 'multiplier')
    // Utiliser les refs pour éviter les closures obsolètes attachées lors de la création du socket
    if (
      autoCashoutEnabledRef.current &&
      myBetRef.current && myBetRef.current.type === 'multiplier' &&
      data.multiplier >= autoCashoutMultiplierRef.current
    ) {
      handleCashout({ auto: true });
    }
    });

    newSocket.on('game-ended', (data) => {
      console.log('🎮 Partie terminée:', data);
      setGameState(prev => ({
        ...prev,
        status: 'finished',
        multiplier: data.finalMultiplier,
        players: prev.players.map(p => ({ ...p, bet: null }))
      }));
      setCanCashout(false);
      setCanPlaceBet(true);
      
      // Afficher le résultat du combat (utilise un snapshot fiable de votre pari)
      const betSnapshot = myBetRef.current;
      if (data.winningCock) {
        if (betSnapshot && betSnapshot.type === 'cock') {
          if (betSnapshot.selectedCock === data.winningCock) {
            setResultMessage({
              type: 'win',
              text: <span className="text-xs sm:text-sm">🎉 Vous avez gagné !</span>
            });
          } else {
            setResultMessage({
              type: 'lose',
              text: <span className="text-xs sm:text-sm">😔 Vous avez perdu...</span>
            });
          }
          setTimeout(() => setResultMessage(null), 2500);
        }
      }
      
      // Réinitialiser le pari courant après avoir affiché le message
      setMyBet(null);
      
      // Ajouter à l'historique
      setGameHistory(prev => [
        {
          multiplier: data.finalMultiplier,
          reason: data.reason,
          timestamp: Date.now(),
          winningCock: data.winningCock
        },
        ...prev.slice(0, 19) // Garder seulement les 20 dernières
      ]);
    });

    newSocket.on('bet-placed', (data) => {
      console.log('🎲 Pari placé:', data);
    });

    newSocket.on('bet-confirmed', (data) => {
      console.log('✅ Pari confirmé:', data);
      setMyBet(data);
      updateBalance(data.newBalance);
      setCanPlaceBet(false);
      setNextRoundBetType(null);
      if (data.type === 'multiplier') {
        setResultMessage({
          type: 'info',
          text: <span className="text-xs sm:text-sm">Vous avez misé sur le temps de jeu !</span>
        });
        setTimeout(() => setResultMessage(null), 2500);
      } else if (data.type === 'cock') {
        setResultMessage({
          type: 'info',
          text: <span className="text-xs sm:text-sm">Vous avez misé sur le coq {data.selectedCock === 'cock1' ? '1' : '2'} !</span>
        });
        setTimeout(() => setResultMessage(null), 2500);
      }
    });

    newSocket.on('players-updated', (data) => {
      console.log('👥 Liste des joueurs mise à jour:', data);
      setGameState(prev => ({
        ...prev,
        players: data.players
      }));
    });

    // Mise à jour du solde émise par le serveur (gains crédités)
    newSocket.on('balance-updated', (data) => {
      console.log('💳 Solde mis à jour:', data);
      if (data && data.userId === user.id && typeof data.newBalance === 'number') {
        updateBalance(data.newBalance);
      }
    });

    newSocket.on('cashout', (data) => {
      console.log('💰 Encaissement:', data);
    });

    newSocket.on('cashout-confirmed', (data) => {
      console.log('✅ Encaissement confirmé:', data);
      setMyBet(null);
      setCanCashout(false);
      if (!data || data.status !== 'pending') {
        if (typeof data?.newBalance === 'number') {
          updateBalance(data.newBalance);
        }
      }
  showMessage({ type: 'win', text: <Coin amount={data.winnings.toFixed(2)} symbol="HTG" /> }, 2500);
    });

    newSocket.on('error', (data) => {
      console.error('❌ Erreur:', data);
      if (data.message === 'Solde insuffisant') {
        setShowInsufficientBalancePopup(true);
      } else {
        setError(data.message);
      }
    });

    return () => {
      newSocket.disconnect();
    };
  }, [user]);

  // Animation du multiplicateur
  useEffect(() => {
    if (gameState.status === 'active' && multiplierRef.current) {
      const animate = () => {
        if (multiplierRef.current) {
          multiplierRef.current.style.transform = `scale(${1 + (gameState.multiplier - 1) * 0.1})`;
        }
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState.multiplier, gameState.status]);

  // Compte à rebours pendant la phase d'attente
  useEffect(() => {
    let countdownInterval;
    
    if (gameState.status === 'waiting' && gameState.countdown > 0) {
      countdownInterval = setInterval(() => {
        setGameState(prev => ({
          ...prev,
          countdown: Math.max(0, prev.countdown - 1)
        }));
      }, 1000);
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [gameState.status, gameState.countdown]);

  // Cleanup pour le timer de message à la destruction du composant
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Appliquer automatiquement la mise en attente quand la nouvelle manche passe à 'waiting'
  useEffect(() => {
    if (
      gameState.status === 'waiting' &&
      pendingBetRef.current &&
      gameState.gameId &&
      lastAppliedGameIdRef.current !== gameState.gameId
    ) {
      console.log('🔁 Application automatique du pari en attente pour la manche:', gameState.gameId);
      placeBet(pendingBetRef.current);
      setPendingBet(null);
      lastAppliedGameIdRef.current = gameState.gameId;
    }
  }, [gameState.status, gameState.gameId]);

  // Affiche la carte seulement après le loader (quand la partie est active)
  useEffect(() => {
    setShowBettersCard(gameState.status === 'active');
  }, [gameState.status]);

  const placeBet = (betData) => {
    if (!socket || !user || !betData.amount) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    if (betData.type === 'cock' && !betData.selectedCock) {
      setError('Veuillez sélectionner un coq');
      return;
    }

    const amount = parseFloat(betData.amount);
    if (isNaN(amount) || amount < 1 || amount > 10000) {
      setError(<>Le montant doit être entre <Coin amount={1} symbol="HTG" /> et <Coin amount={10000} symbol="HTG" /></>);
      return;
    }

    if (amount > user.balance) {
  setError('Solde insuffisant');
  setShowInsufficientBalancePopup(true);
  setTimeout(() => setShowInsufficientBalancePopup(false), 2500);
  return;
    }

    // Empêcher un pari sur l'autre jeu s'il y a déjà un type de pari programmé pour la prochaine manche
    if (nextRoundBetTypeRef.current && nextRoundBetTypeRef.current !== betData.type) {
      setError('Vous avez déjà un pari programmé pour l\'autre jeu');
      return;
    }

    // Empêcher un pari sur l'autre jeu s'il y a déjà un pari en attente
    if (pendingBetRef.current && pendingBetRef.current.type && pendingBetRef.current.type !== betData.type) {
      setError('Vous avez déjà un pari en attente pour l\'autre jeu');
      return;
    }

    // Si la partie est en cours, sauvegarder le pari pour la prochaine partie
    if (gameState.status === 'active' || gameState.status === 'finished') {
      setPendingBet(betData);
      setNextRoundBetType(betData.type);
      setError(null);
      return;
    }

    // Si on peut parier maintenant (nouvelle partie)
    if (gameState.status === 'waiting') {
      socket.emit('place-bet', {
        ...betData,
        userId: user.id,
        amount: amount,
        autoCashoutEnabled: autoCashoutEnabled,
        autoCashoutMultiplier: autoCashoutMultiplier, // Envoyer la valeur du multiplicateur choisie par l'utilisateur
      });
    }
  };

  const handlePlaceCockBet = () => {
    if (!selectedCock) {
      setError('Veuillez sélectionner un coq');
      return;
    }
    setBetType('cock');
    placeBet({
      amount: betAmount,
      selectedCock: selectedCock,
      type: 'cock'
    });
  };

  const cancelPendingBet = () => {
    if (!pendingBet) return;

    setPendingBet(null);
    setNextRoundBetType(null);
    setBetType(null);
    setError(null);

    // Afficher un message de confirmation
    showMessage({ type: 'info', text: 'Pari annulé avec succès' }, 2000);
  };

  const handlePlaceMultiplierBet = () => {
    console.log('handlePlaceMultiplierBet called - betAmount=', betAmount, 'autoCashoutEnabled=', autoCashoutEnabled);
    setBetType('multiplier');
    placeBet({
      amount: betAmount,
      type: 'multiplier',
      selectedCock: null // Assurer que la propriété existe
    });
  };

  const showMessage = (message, duration = 2500) => {
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    setResultMessage(message);
    messageTimeoutRef.current = setTimeout(() => setResultMessage(null), duration);
  };

  const handleCashout = (opts = { auto: false }) => {
    if (!socket || !user || !myBet || !canCashout) {
      setError('Impossible d\'encaisser maintenant');
      return;
    }

    // Empêcher double encaissement
    if (cashoutAt) return;

    const currentMult = gameState.multiplier || 1;
    setCashoutAt(currentMult);

    // Calcul du gain estimé
    let estWinnings;
    if (autoCashoutEnabled && myBet.type === 'multiplier' && myBet.autoCashoutMultiplier) {
      estWinnings = (parseFloat(myBet.amount) || 0) * myBet.autoCashoutMultiplier;
    } else {
      estWinnings = (parseFloat(myBet.amount) || 0) * (parseFloat(gameState.multiplier) || 1);
    }

    // Message spécifique pour auto-cashout: afficher montant encaissé
    if (opts.auto) {
      showMessage({ type: 'win', text: <>Vous avez encaissé <Coin amount={estWinnings.toFixed(2)} symbol="HTG" /></> }, 2500);
    } else {
      showMessage({ type: 'win', text: <>Vous avez encaissé <Coin amount={estWinnings.toFixed(2)} symbol="HTG" /></> }, 2500);
    }

    setCanCashout(false);

    // Ne pas effacer immédiatement le message ni cashoutAt — laisser le message s'afficher.
    const duration = opts.auto ? 2500 : 2500;
    setTimeout(() => {
      resetBetState();
    }, duration + 150);

    socket.emit('cashout', { userId: user.id });
  };

  const setPresetAmount = (amount) => {
    setBetAmount(amount.toString());
  };

  const adjustBetAmount = (increment) => {
    const current = parseFloat(betAmount) || 0;
    const newAmount = Math.max(1, Math.min(10000, current + increment));
    setBetAmount(newAmount.toString());
  };

  const adjustAutoCashout = (increment) => {
    const current = parseFloat(autoCashoutMultiplier) || 2.0;
    const newMultiplier = Math.max(1.1, Math.min(100, current + increment));
    setAutoCashoutMultiplier(newMultiplier);
  };

  const getStatusColor = () => {
    switch (gameState.status) {
      case 'waiting': return 'bg-yellow-500';
      case 'active': return 'bg-green-500';
      case 'finished': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (gameState.status) {
      case 'waiting': return 'En attente';
      case 'active': return 'En cours';
      case 'finished': return 'Terminé';
      default: return 'Inconnu';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Veuillez vous connecter pour jouer</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-8">
        
        {/* Statut de connexion (admin uniquement) */}
        {user && user.role === 'admin' && (
          <div className="mb-6">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              isConnected 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-2 ${
                isConnected ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              {isConnected ? 'Connecté' : 'Déconnecté'}
            </div>
          </div>
        )}

        {/* Erreur */}
        {/* {error && (
           <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )} */}

        {/* Message solde insuffisant animé */}
        {showInsufficientBalancePopup && (
          <div
            className="fade-message show bg-red-700 text-white"
            style={{
              position: 'fixed',
              top: 30,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              padding: '1rem 2rem',
              borderRadius: '0.5rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              pointerEvents: 'none',
              opacity: 1,
              transition: 'opacity 0.5s',
              animation: 'fadeInOut 2.5s'
            }}
          >
            💰 Solde insuffisant&nbsp;: Vous n'avez pas assez de fonds pour placer ce pari.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-0">
          {/* Zone de jeu principale élargie */}
          <div className="md:col-span-1 w-full">
            <div className="bg-white rounded-lg shadow-lg p-2 sm:p-4 md:p-8 w-full md:w-[700px] lg:w-[900px] mx-auto">
              {/* Statut du jeu */}
              <div className="flex flex-col sm:flex-row items-center justify-between mb-2 gap-2">
                <div className="flex items-center w-full justify-between">
                  {/* Logo ou autres éléments à gauche ici si besoin */}
                  <div></div>
                  {/* Menu hamburger à droite */}
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="p-1 rounded-md transition-colors group"
                      style={{ minWidth: 32, minHeight: 32, background: 'transparent' }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ stroke: '#111' }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                          className="transition-colors group-hover:stroke-white"
                        />
                      </svg>
                      <style>{`
                        .group:hover {
                          background: #111 !important;
                        }
                        .group:hover svg {
                          stroke: #fff !important;
                        }
                      `}</style>
                    </button>
                    {isMenuOpen && (
                      <div style={{ position: 'absolute', top: '80%', right: 0, marginTop: '0px', zIndex: 9999 }}>
                        <HamburgerMenu
                          isOpen={isMenuOpen}
                          onClose={() => setIsMenuOpen(false)}
                          onMenuItemClick={(item) => {
                            setMenuCard(item);
                          }}
                        />
                      </div>
                    )}
                    {/* Card menu affichée selon le choix */}
                    {menuCard === 'history' && (
                      <>
                        <div className="fixed inset-0 flex items-center justify-center bg-black/30 px-2" style={{zIndex: 9999}} onClick={() => setMenuCard(null)}>
                          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md relative" onClick={e => e.stopPropagation()}>
                            <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800" onClick={() => setMenuCard(null)}>&times;</button>
                            <h2 className="text-lg font-bold mb-4">Mon historique de mise</h2>
                            <table className="w-full text-xs mb-2">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="p-2">Date</th>
                                  <th className="p-2">Montant misé</th>
                                  <th className="p-2">Statut</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userBets.map((bet, idx) => {
                                  const statut = bet.status || 'En attente';
                                  return (
                                    <tr key={idx}>
                                      <td className="p-2">{bet.date}</td>
                                      <td className="p-2">{bet.amount}</td>
                                      <td className="p-2">{statut}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            <div className="text-xs text-gray-600">Total parties misées: {userBets.length}</div>
                          </div>
                        </div>
                        {showInsufficientBalancePopup && (
                          <div
                            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[9999]"
                            onClick={() => setShowInsufficientBalancePopup(false)}
                          >
                            <div
                              className="bg-white rounded-lg shadow-lg p-6 text-center relative z-[10000]"
                              onClick={e => e.stopPropagation()}
                            >
                              <p className="text-lg font-semibold mb-4">Solde insuffisant</p>
                              <button
                                className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                                onClick={() => setShowInsufficientBalancePopup(false)}
                              >
                                Fermer
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {menuCard === 'how-to-play' && (
                      <div className="fixed inset-0 flex items-center justify-center bg-black/30 px-2" style={{zIndex: 9999}} onClick={() => setMenuCard(null)}>
                        <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md relative" onClick={e => e.stopPropagation()}>
                          <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800" onClick={() => setMenuCard(null)}>&times;</button>
                          <h2 className="text-lg font-bold mb-4">Comment jouer ?</h2>
                          <ul className="list-disc pl-5 text-sm text-gray-700">
                            <li>Choisissez le montant à miser.</li>
                            <li>Sélectionnez un coq ou misez sur le multiplicateur.</li>
                            <li>Attendez le début de la partie.</li>
                            <li>Encaissez vos gains si votre pari est gagnant.</li>
                          </ul>
                        </div>
                      </div>
                    )}
                    {menuCard === 'rules' && (
                      <div className="fixed inset-0 flex items-center justify-center bg-black/30 px-2" style={{zIndex: 9999}} onClick={() => setMenuCard(null)}>
                        <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md relative" onClick={e => e.stopPropagation()}>
                          <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800" onClick={() => setMenuCard(null)}>&times;</button>
                          <h2 className="text-lg font-bold mb-4">Règle de jouer</h2>
                          <ul className="list-disc pl-5 text-sm text-gray-700">
                            <li>Un seul pari par partie.</li>
                            <li>Le coq avec le moins de paris gagne.</li>
                            <li>Les gains sont crédités automatiquement.</li>
                            <li>Respectez les limites de mise.</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages au-dessus du conteneur 3D */}
              {myBet && gameState.status === 'active' && canCashout && (
                <div className="mt-6 text-center">
                  {/* <div className="mb-4 p-4 bg-green-50 rounded-lg">
                    <p className="text-green-800 font-medium text-xs sm:text-base">
                      Votre pari: <Coin amount={myBet.amount} symbol="HTG" /> {myBet.type === 'cock' ? (<>
                        sur Coq {myBet.selectedCock === 'cock1' ? '1' : '2'}
                      </>) : 'sur le temps de jeu'}
                    </p>
                    {myBet.type === 'multiplier' && (
                      <p className="text-green-600 text-xs sm:text-base">
                        Gains potentiels: <Coin amount={(myBet.amount * gameState.multiplier).toFixed(2)} symbol="HTG" />
                      </p>
                    )}
                  </div> */}
                </div>
              )}

              
              {/* {gameState.status === 'finished' && (
                <div className="mt-6 text-center">
                  <div className={`p-4 rounded-lg ${
                    gameState.multiplier >= 2 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}>
                    <p className="text-lg font-medium text-xs sm:text-base">
                      Partie terminée à {gameState.multiplier.toFixed(2)}x
                    </p>
                  </div>
                </div>
              )} */}

              {pendingBet && (
                <div className="mt-6 text-center">
                  {/* <div className="p-4 bg-blue-50 rounded-lg text-blue-800">
                    <p className="text-lg font-medium text-xs sm:text-base">
                      ⏳ Pari en attente pour la prochaine partie
                    </p>
                    <p className="text-sm text-xs sm:text-base">
                      <Coin amount={pendingBet.amount} symbol="HTG" /> {pendingBet.type === 'cock' ? (<>
                        sur Coq {pendingBet.selectedCock === 'cock1' ? '1' : '2'}
                      </>) : 'sur le temps de jeu'}
                    </p>
                  </div> */}
                </div>
              )}

              

              <div className="grid grid-cols-1 gap-0 mb-0">
                
                <div className="mb-4">
                  
                  {/* Menu hamburger */}
                  <div className="mb-4 flex justify-end">
                    
                    {/* <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="p-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button> */}
                  </div>

                  {/* Menu hamburger déroulant
                  {isMenuOpen && (
                    <div className="mb-4">
                      <HamburgerMenu
                        onClose={() => setIsMenuOpen(false)}
                        user={user}
                      />
                    </div>
                  )} */}

                  {/* Combat 3D élargi */}
                  <div className="relative h-[px] md:h-[380px]">
                    {/* Liste des joueurs qui ont parié - nouvelle carte */}
              {showBettersCard && (
                <div className="overflow-y-auto max-h-56 px-4 pb-3 betters-scroll">
                  <BettersListCard bets={gameState.players.filter(p => p.bet)} />
                </div>
              )}
                    <CockFight3D
                      gameState={gameState}
                      myBet={myBet}
                      onCockWin={(winningCock) => {
                        console.log(`🏆 Coq ${winningCock === 'cock1' ? '1' : '2'} a gagné!`);
                      }}
                    />
                    
                    {gameState.status === 'waiting' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-40">
                        <div className="flex flex-col items-center w-full max-w-xs mx-auto p-4">
                          <svg className="animate-spin h-6 w-6 sm:h-8 sm:w-8 text-orange-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                          </svg>
                          <p className="text-xs sm:text-base font-semibold text-white mb-2 text-center">En attente de la prochaine partie</p>
                          {gameState.countdown > 0 && (
                            <>
                              <div className="w-full h-1 sm:h-2 bg-white/20 rounded-full overflow-hidden mb-2">
                                <div
                                  className="h-full bg-orange-400 transition-[width] duration-1000 ease-linear"
                                  style={{ width: `${(gameState.countdown / WAIT_COUNTDOWN) * 100}%` }}
                                />
                              </div>
                              <div className="text-center text-lg sm:text-2xl font-bold tabular-nums text-orange-300">
                                {gameState.countdown}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {/* Interface de paris avancée */}
                  <div className="bg-gray-800 rounded-lg p-3 text-white text-xs">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Section Montant du Pari */}
                      <div>
                        <h3 className="text-xs font-semibold mb-1">Montant du Pari</h3>
                        
                        {/* Contrôles MIN/MAX */}
                        <div className="flex items-center mb-4">
                          <button
                            onClick={() => adjustBetAmount(-1)}
                            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-l text-base font-bold"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={betAmount}
                            onChange={(e) => setBetAmount(e.target.value)}
                            className="flex-1 bg-gray-700 text-white text-center py-1 px-2 border-0 focus:ring-2 focus:ring-orange-500 no-spinner text-base"
                            min="1"
                            max="10000"
                            style={{ MozAppearance: 'textfield' }}
                          />
                          <button
                            onClick={() => adjustBetAmount(1)}
                            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-r text-base font-bold"
                          >
                            +
                          </button>
                        </div>

                        {/* Boutons de montants prédéfinis */}
                        <div className="grid grid-cols-2 gap-1">
                          {presetAmounts.map((amount) => {
                            let display = amount;
                            let value = amount;
                            if (amount === 'MIN') {
                              display = 'MIN';
                              value = 5;
                            } else if (amount === 'MAX') {
                              display = 'MAX';
                              value = 500;
                            }
                            let colorClass = '';
                            if (amount === 'MIN') {
                              colorClass = parseFloat(betAmount) === value
                                ? 'bg-red-800 text-white'
                                : 'bg-red-700 text-white hover:bg-red-900';
                            } else if (amount === 'MAX') {
                              colorClass = parseFloat(betAmount) === value
                                ? 'bg-green-800 text-white'
                                : 'bg-green-700 text-white hover:bg-green-900';
                            } else {
                              colorClass = parseFloat(betAmount) === value
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-700 hover:bg-gray-600 text-white';
                            }
                            return (
                              <button
                                key={amount}
                                onClick={() => setPresetAmount(value)}
                                className={`p-1 rounded text-xs font-medium transition-colors ${colorClass}`}
                              >
                                {display}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* <div className={`${(betType === 'cock' || (pendingBet && pendingBet.type === 'cock') || nextRoundBetType === 'cock') ? 'opacity-50 pointer-events-none' : ''}`}><div className="flex items-center mb-4"><h3 className="text-xs font-semibold mr-1 mb-0">Auto Cashout</h3><button type="button" onClick={(e) => { e.stopPropagation(); console.log('🔔 autoCashout toggle clicked ->', !autoCashoutEnabled); setAutoCashoutEnabled(!autoCashoutEnabled); }} disabled={betType === 'cock'} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoCashoutEnabled ? 'bg-orange-500' : 'bg-gray-600'}`} aria-label="Activer/Désactiver Auto Cashout"><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoCashoutEnabled ? 'translate-x-6' : 'translate-x-1'}`}/></button></div><div className="flex items-center"><button onClick={() => adjustAutoCashout(-0.1)} disabled={betType === 'cock'} className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-l text-xs">-</button><div className="flex-1 bg-gray-700 text-center py-1 px-2 text-xs">{autoCashoutMultiplier.toFixed(1)}x</div><button onClick={() => adjustAutoCashout(0.1)} disabled={betType === 'cock'} className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-r text-xs">+</button></div><div className="mt-4"><button onClick={handleCashout} disabled={autoCashoutEnabled || gameState.status === 'finished'} className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-colors">{autoCashoutEnabled ? "Retrait indisponible (Auto Cashout actif)" : gameState.status === 'finished' ? "Combat terminé - Retrait indisponible" : "Retirer ma mise"}</button></div></div> */}

                      {/* Section Sélection Coq et Bouton Pari */}
                      <div className={(betType === 'multiplier' || (pendingBet && pendingBet.type === 'multiplier') || nextRoundBetType === 'multiplier') ? 'opacity-50 pointer-events-none' : ''}>
                        <h3 className="text-xs font-semibold mb-1">Sélection Coq</h3>
                        
                        <div className="grid grid-cols-2 gap-1 mb-2">
                          {['cock1', 'cock2'].map((cock) => {
                            
                            return (
                              <div key={cock} className="flex flex-col items-center">
                                                                <button
                                  onClick={() => setSelectedCock(cock)}
                                  disabled={betType === 'multiplier'}
                                  className={`p-1 border rounded text-center transition-colors text-xs ${
                                    selectedCock === cock
                                      ? 'border-orange-500 bg-orange-500 text-white'
                                      : 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-white'
                                  }`}
                                >
                                  <div className="text-xs mb-0.5">🐓</div>
                                  <div className="text-[10px]">Coq {cock === 'cock1' ? '1' : '2'}</div>
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        <button
                          onClick={pendingBet && pendingBet.type === 'cock' ? cancelPendingBet : handlePlaceCockBet}
                          disabled={!selectedCock || !betAmount || betType === 'multiplier' || (pendingBet && pendingBet.type === 'multiplier') || nextRoundBetType === 'multiplier'}
                          className={`w-full font-bold py-2 px-3 rounded text-[11px] transition-colors ${
                            pendingBet && pendingBet.type === 'cock'
                              ? 'bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white'
                              : 'bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white'
                          }`}
                        >
                          {pendingBet && pendingBet.type === 'cock' ? 'Annuler(en attente du prochain tour)' : 'PARIER'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Message victoire/défaite animé */}
              {resultMessage && (
                <div
                  className={`fade-message show ${
                    resultMessage.type === 'win'
                      ? 'bg-green-600 text-white'
                      : resultMessage.type === 'lose'
                      ? 'bg-red-700 text-white'
                      : 'bg-blue-600 text-white' // Cas pour 'info'
                  }`}
                  style={{ animation: 'fadeInOut 2.5s' }}
                >
                  {resultMessage.text}
                </div>
              )}
                            {/* Panneau de mise déplacé à droite du conteneur 3D (et empilé en responsive) */}
            </div>
          </div>

          {/* Sidebar */}
        </div>
      </div>
    </div>
  );
};

export default Game; 
// fin de Game